import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { BRAND, REGIONS } from "@/data/site";
import { Eyebrow, FadeIn, PageHero, usePageMeta } from "@/components/blocks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-ink/25 bg-transparent px-4 py-3.5 text-sm text-ink placeholder:text-ink/35 transition-colors focus:border-ink focus:outline-none";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60";

const TOPICS = ["General", "Coaching", "Corporate", "Press / Partnerships"];

export default function Contact() {
  usePageMeta(
    "Contact | StrengthLeap",
    "Speak with the StrengthLeap team — coaching enquiries, corporate wellness programs and partnerships."
  );

  const [form, setForm] = useState({ name: "", email: "", topic: "General", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please complete name, email and message.");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("done");
    } catch {
      setStatus("idle");
      setError("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <main className="bg-bone text-ink">
      <PageHero
        testid="contact-page"
        eyebrow="Contact"
        lines={["Speak with", "the team."]}
        copy="Coaching enquiries, corporate wellness programs and partnerships — one inbox, a human reply."
      />

      <section className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto grid max-w-[1440px] gap-16 lg:grid-cols-[1fr_1.4fr] lg:gap-24">
          <div>
            <FadeIn>
              <Eyebrow tone="ink">Direct</Eyebrow>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-link"
                className="mt-6 block font-display text-2xl font-black tracking-tight underline decoration-lime decoration-2 underline-offset-8 transition-colors hover:text-ink/70 sm:text-3xl"
              >
                {BRAND.email}
              </a>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">Coaching clients in</p>
              <p className="mt-4 font-mono text-[11px] uppercase leading-loose tracking-[0.2em] text-ink/75">
                {REGIONS.join("  /  ")}
              </p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <p className="mt-12 max-w-sm text-sm leading-relaxed text-smoke">
                Starting coaching? The assessment is the fastest route — it gives us everything we need to recommend
                the right program.
              </p>
            </FadeIn>
          </div>

          {status === "done" ? (
            <FadeIn>
              <div data-testid="contact-confirmation" className="flex h-full flex-col justify-center border border-ink/15 p-10 sm:p-14">
                <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-ink/60">
                  <span className="flex h-6 w-6 items-center justify-center bg-lime">
                    <Check className="h-4 w-4 text-white" />
                  </span>
                  Message received
                </p>
                <h2 className="mt-6 font-display text-3xl font-black uppercase tracking-tight sm:text-5xl">
                  We’ll be in touch.
                </h2>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-smoke sm:text-base">
                  Thanks for reaching out. A member of the StrengthLeap team will reply to your email.
                </p>
              </div>
            </FadeIn>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
              <div className="grid gap-8 sm:grid-cols-2">
                <FadeIn>
                  <label htmlFor="cf-name" className={labelCls}>
                    Name *
                  </label>
                  <input id="cf-name" data-testid="contact-input-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
                </FadeIn>
                <FadeIn delay={0.05}>
                  <label htmlFor="cf-email" className={labelCls}>
                    Email *
                  </label>
                  <input id="cf-email" data-testid="contact-input-email" type="email" required className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                </FadeIn>
              </div>
              <FadeIn delay={0.1}>
                <label htmlFor="cf-topic" className={labelCls}>
                  Topic
                </label>
                <select id="cf-topic" data-testid="contact-input-topic" className={inputCls} value={form.topic} onChange={set("topic")}>
                  {TOPICS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </FadeIn>
              <FadeIn delay={0.15}>
                <label htmlFor="cf-message" className={labelCls}>
                  Message *
                </label>
                <textarea id="cf-message" data-testid="contact-input-message" rows={6} required className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us what you need…" />
              </FadeIn>
              {error && (
                <p data-testid="contact-form-error" className="border border-red-900/40 bg-red-950/10 px-4 py-3 text-sm text-red-800">
                  {error}
                </p>
              )}
              <FadeIn delay={0.2}>
                <button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  disabled={status === "sending"}
                  className="group inline-flex items-center gap-3 bg-ink px-8 py-5 font-mono text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-coal disabled:opacity-60"
                >
                  {status === "sending" ? "Sending…" : "Send Message"}
                  <ArrowUpRight className="h-4 w-4 text-lime transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>
              </FadeIn>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
